/**
 * Interpret /collections/check-collection API responses for the UI.
 */

const STORE_LABELS = {
  milvus: 'Milvus',
  pinecone: 'Pinecone',
};

export const humanizeVectorStoreError = (rawError, vectorStoreType = 'milvus') => {
  const storeKey = String(vectorStoreType || 'milvus').toLowerCase();
  const storeLabel = STORE_LABELS[storeKey] || 'Vector database';
  const raw = String(rawError || '').trim();
  const lower = raw.toLowerCase();

  if (!raw) {
    return `${storeLabel} is not reachable. Please ensure the vector database is running, then try again.`;
  }

  if (lower.includes('milvus_host') && lower.includes('not set')) {
    return `${storeLabel} is not configured on this server. Please contact your administrator.`;
  }

  if (
    lower.includes('milvusexception')
    || lower.includes('fail connecting to server')
    || lower.includes('server unavailable')
    || lower.includes('connection refused')
    || lower.includes('timed out')
    || lower.includes('timeout')
    || lower.includes('illegal connection params')
  ) {
    return `${storeLabel} is not running or cannot be reached right now. Please start the vector database service and try again.`;
  }

  if (lower.includes('pinecone') && lower.includes('api key')) {
    return 'Pinecone is not configured on this server. Please contact your administrator.';
  }

  // Fallback: avoid dumping raw exception text into the UI.
  return `${storeLabel} could not be reached. Please ensure it is running and try again.`;
};

/**
 * @param {object|undefined} data - Axios response body
 * @param {string} vectorStoreType - e.g. "milvus"
 * @returns {{ kind: 'available'|'exists'|'connection_error', message?: string }}
 */
export const interpretCollectionCheckResponse = (data, vectorStoreType = 'milvus') => {
  if (!data || typeof data !== 'object') {
    return {
      kind: 'connection_error',
      message: humanizeVectorStoreError('', vectorStoreType),
    };
  }

  if (data.connection_error || data.error) {
    return {
      kind: 'connection_error',
      message: humanizeVectorStoreError(data.error, vectorStoreType),
    };
  }

  if (data.available) {
    return { kind: 'available' };
  }

  return { kind: 'exists' };
};

/**
 * Apply collection check result to React state setters.
 */
export const applyCollectionCheckResult = (
  result,
  { setCollectionAvailable, setCollectionConnectionError, setShowCollectionStatus },
) => {
  setShowCollectionStatus(true);
  if (result.kind === 'available') {
    setCollectionAvailable(true);
    setCollectionConnectionError('');
    return;
  }
  if (result.kind === 'connection_error') {
    setCollectionAvailable(false);
    setCollectionConnectionError(
      result.message || 'The vector database is not reachable. Please try again later.',
    );
    return;
  }
  setCollectionAvailable(false);
  setCollectionConnectionError('');
};
